import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  buildAuthorizationHeader,
  getRefreshToken,
  getToken,
  getTokenType,
  isRefreshTokenExpired,
  isTokenExpired,
  isTokenExpiringSoon,
  removeRefreshToken,
  removeToken,
  removeTokenType,
  setRefreshToken,
  setToken,
  setTokenType,
} from './common';

// Add custom request config property to Axios types
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

// Avoid duplicate refresh calls when multiple requests happen concurrently
let isRefreshing = false;
let refreshPromise: Promise<{
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
} | null> | null = null;

const isRefreshRequest = (url?: string) => {
  if (!url) return false;
  // Keep legacy "/refresh_token" check and add the real refresh endpoint used by this app
  return url.includes('/refresh_token') || url.includes('/api/v1/auths/sessions/refresh');
};

const redirectToLogin = () => {
  // Force a hard redirect so Redux initial state re-reads localStorage (token cleared)
  if (typeof window !== 'undefined') window.location.assign('/login');
};

function getAxios(baseURL: string) {
  const ins = axios.create({
    baseURL,
  });

  // Separate client for refresh calls (no interceptors) to avoid infinite loops
  const refreshClient = axios.create({ baseURL });

  // Request interceptor
  ins.interceptors.request.use(async function (config) {
    if (!config.headers) {
      config.headers = {} as any;
    }

    // Skip auth for refresh token endpoint to prevent infinite loop
    if (config.skipAuth || isRefreshRequest(config.url)) {
      (config.headers as any)['Content-Type'] = 'application/json';
      return config;
    }

    let accessToken = getToken();
    const refreshToken = getRefreshToken();

    // Proactive refresh: if token is expired OR about to expire, refresh before any API call.
    // This prevents errors when the user navigates (e.g. list -> detail) right as the token expires.
    const shouldRefresh =
      !!accessToken && (isTokenExpired(accessToken) || isTokenExpiringSoon(accessToken));

    if (shouldRefresh) {
      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        removeToken();
        removeRefreshToken();
        removeTokenType();
        redirectToLogin();
        throw new axios.CanceledError('No valid refresh token');
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const res = await refreshClient.post(
              'api/v1/auths/sessions/refresh',
              { refreshToken },
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              },
            );

            const newAccessToken =
              res?.data?.content?.auth?.token?.accessToken ??
              res?.data?.content?.token?.accessToken ??
              res?.data?.auth?.token?.accessToken ??
              res?.data?.token?.accessToken ??
              res?.data?.accessToken;
            const newRefreshToken =
              res?.data?.content?.auth?.token?.refreshToken ??
              res?.data?.content?.token?.refreshToken ??
              res?.data?.auth?.token?.refreshToken ??
              res?.data?.token?.refreshToken ??
              res?.data?.refreshToken;
            const newTokenType =
              res?.data?.content?.auth?.token?.tokenType ??
              res?.data?.content?.token?.tokenType ??
              res?.data?.auth?.token?.tokenType ??
              res?.data?.token?.tokenType ??
              res?.data?.tokenType;

            if (!newAccessToken) return null;

            setToken(newAccessToken);
            if (newRefreshToken) setRefreshToken(newRefreshToken);
            if (newTokenType) setTokenType(newTokenType);

            return {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
              tokenType: newTokenType,
            };
          } catch (e) {
            return null;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();
      }

      const refreshed = await refreshPromise;
      if (!refreshed?.accessToken) {
        removeToken();
        removeRefreshToken();
        removeTokenType();
        redirectToLogin();
        throw new axios.CanceledError('Token refresh failed');
      }
      accessToken = refreshed.accessToken;
    }

    const authorizationHeader = buildAuthorizationHeader(accessToken, getTokenType());
    if (authorizationHeader) {
      (config.headers as any)['Authorization'] = authorizationHeader;
    }
    (config.headers as any)['Content-Type'] = 'application/json';
    return config;
  });

  // Response interceptor for handling token refresh
  ins.interceptors.response.use(
    function (response: AxiosResponse) {
      return response;
    },
    async function (error: AxiosError) {
      const originalRequest = error.config as any;

      // Skip refresh logic for refresh token endpoint to prevent infinite loop
      if (originalRequest.skipAuth || isRefreshRequest(originalRequest.url)) {
        return Promise.reject(error);
      }

      // DISABLED: Let checkAuthStatus handle token refresh instead
      // This prevents duplicate refresh calls
      return Promise.reject(error);

      return Promise.reject(error);
    },
  );

  return ins;
}

const apiBaseUrl = import.meta.env.VITE_API_URL ?? '';

if (!apiBaseUrl) {
  console.warn(
    'VITE_API_URL environment variable is not defined. API requests will use relative URLs.',
  );
}

// Create an axios instance with the base URL
export const API = getAxios(apiBaseUrl);
